import { DisplayStyle } from '../models/transaction-metadata.model'

/**
 * The display-style rule table: one entry per {@link DisplayStyle} the metadata document authors.
 *
 * A field's Display Style column is the ONLY thing that decides how its value is written, and this
 * table is the only place that decision lives. Adding a style is adding one entry here; no consumer
 * branches on a style name, and none should — a `switch` on style outside this file is the bug this
 * table exists to prevent.
 *
 * Style is orthogonal to the CONTROL a field edits with. `controlFor` picks the control from List
 * Type and Data Type and deliberately never consults style, because style says nothing about what
 * may be typed. This table answers the other question: given a value and a style, what string goes
 * on screen, what string goes into a native control, and what comes back out.
 *
 * Modal-local on purpose: the grid's renderers are ag-Grid value-formatter shaped and carry grid
 * concerns, while the modal needs plain strings for form controls and the comparison column. The
 * grid's `formatAmount` is deliberately NOT reused — see {@link PRETTY_DIGITS} for why money's
 * fixed two decimals are the wrong rule for this style.
 */
export interface DisplayStyleRule {
  format(value: unknown): string
  toEdit(value: unknown): string
  /** Back to a storable value, from either the form {@link format} writes or the raw form itself. */
  toRaw(input: unknown): unknown
}

const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === 'string' && value.trim() === '')

/**
 * The magnitude of a number as it may be spelled: either plain digits or digits grouped in threes
 * by commas (`1,234,567`), then an optional fraction, then an optional exponent (`String(1e-7)` is
 * how JavaScript spells a small number, so the form `toEdit` writes must read back).
 */
const MAGNITUDE = String.raw`(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?`

/** A whole spelling: an optional sign, OR one pair of accounting parentheses around the magnitude. */
const NUMBER_SPELLING = new RegExp(
  String.raw`^(?:([+-])?(${MAGNITUDE})|\((${MAGNITUDE})\))$`,
)

/**
 * A number from a number or from its spelling; `null` when the value is not a number.
 *
 * The WHOLE spelling is checked before anything is stripped, so malformed text is refused rather
 * than read as some other amount: `12)3`, `1,2,3`, `()` and `(-5)` are not numbers, even though
 * removing their parentheses and commas would leave digits behind. Accepted: `1200`, `-1,200.5`,
 * `+3`, `.5`, `1e-7` and the accounting negative `(1,234.50)`.
 */
export const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const raw = String(value).trim()
  const spelled = NUMBER_SPELLING.exec(raw)
  if (!spelled) return null
  const [, sign, signed, wrapped] = spelled
  const parsed = Number((signed ?? wrapped).replace(/,/g, ''))
  if (!Number.isFinite(parsed)) return null
  const negative = wrapped !== undefined || sign === '-'
  // `-0` is still zero; do not let a sign survive on it.
  if (parsed === 0) return 0
  return negative ? -parsed : parsed
}

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

const daysIn = (year: number, month: number): number =>
  month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]

/** The time part of an ISO timestamp: `T` + `HH:mm`, optional seconds and fraction, optional zone. */
const ISO_TIME = String.raw`T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d)?`

const ISO_DATE = new RegExp(
  String.raw`^(\d{4})-(\d{2})-(\d{2})(?:${ISO_TIME})?$`,
)
const US_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/

/**
 * `yyyy-MM-dd` from the ISO form the API sends (a date, or a timestamp whose date part is taken),
 * or the US form `format` writes; `''` when the value is neither, has anything after it, or names a
 * day its month does not have (`17/07/2026` is a day-first date, not the seventeenth month;
 * `02/31/2026` and `2026-02-29` are not on any calendar, while `2028-02-29` is).
 *
 * Exported for the save assembly (PVAL-26440), which sends a date in exactly this form.
 */
export const asIsoDate = (value: unknown): string => {
  const raw = String(value).trim()
  const valid = (year: string, month: string, day: string): string => {
    const y = Number(year)
    const m = Number(month)
    const d = Number(day)
    return m >= 1 && m <= 12 && d >= 1 && d <= daysIn(y, m)
      ? `${year}-${month}-${day}`
      : ''
  }
  const iso = ISO_DATE.exec(raw)
  if (iso) return valid(iso[1], iso[2], iso[3])
  const us = US_DATE.exec(raw)
  return us ? valid(us[3], us[1], us[2]) : ''
}

/** `default` — as the source sent it. */
const DEFAULT_RULE: DisplayStyleRule = {
  format: value => (isBlank(value) ? '' : String(value)),
  // Nothing was applied, so there is nothing to reveal.
  toEdit: value => (isBlank(value) ? '' : String(value)),
  toRaw: input => input,
}

/** `prettyNumber` digits — at least two decimals, and no cap. */
const PRETTY_DIGITS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 20,
}

/** Negatives carry a minus sign, NOT accounting parentheses. */
const formatPretty = (value: number): string =>
  value.toLocaleString('en-US', PRETTY_DIGITS)

/** `prettyNumber` — grouped digits, negatives in parentheses; stored as a number. */
const PRETTY_NUMBER_RULE: DisplayStyleRule = {
  format: value => {
    if (isBlank(value)) return ''
    const numeric = asNumber(value)
    return numeric === null ? String(value) : formatPretty(numeric)
  },
  toEdit: value => {
    if (isBlank(value)) return ''
    const numeric = asNumber(value)
    return numeric === null ? String(value) : String(numeric)
  },
  toRaw: input => {
    if (isBlank(input)) return input
    // A spelling `asNumber` refuses is KEPT as typed, never rewritten into some other amount, so
    // the save assembly sees it and blocks Save naming the field.
    return asNumber(input) ?? input
  },
}

/** `dateUS` — `MM/DD/YYYY` to read and to type; stored as the ISO form the API speaks. */
const DATE_US_RULE: DisplayStyleRule = {
  format: value => {
    if (isBlank(value)) return ''
    const iso = asIsoDate(value)
    if (!iso) return String(value)
    const [year, month, day] = iso.split('-')
    return `${month}/${day}/${year}`
  },
  toEdit: value => (isBlank(value) ? '' : asIsoDate(value)),
  toRaw: input => {
    if (isBlank(input)) return input
    return asIsoDate(input) || input
  },
}

export const DISPLAY_STYLES: Readonly<Record<DisplayStyle, DisplayStyleRule>> =
  {
    default: DEFAULT_RULE,
    prettyNumber: PRETTY_NUMBER_RULE,
    dateUS: DATE_US_RULE,
  }

/**
 * Format a raw field value for display. A blank value always renders as the empty string — the
 * modal shows nothing rather than an em dash, so an unresolvable comparison key is visually silent
 * (BC-8) rather than looking like a deliberate "no value".
 */
export function formatFieldValue(value: unknown, style: DisplayStyle): string {
  return DISPLAY_STYLES[style].format(value)
}

export function editValue(value: unknown, style: DisplayStyle): string {
  return DISPLAY_STYLES[style].toEdit(value)
}

/** What was typed (or derived, or seeded), back in storable form. */
export function rawValueFrom(input: unknown, style: DisplayStyle): unknown {
  return DISPLAY_STYLES[style].toRaw(input)
}
